<template>
  <Modal
    v-model:visible="isVisible"
    class="parcel-scheduling-modal"
    content-class="flex flex-column flex-gap-1"
    :header="t('parcelScheduling.scheduleParcelPickup')"
    size="lg"
  >
    <template #default>
      <MessageContainer
        :messages="schedulingErrorMessages"
        message-class="rygen-message"
      />

      <RygenButton
        v-if="isInAppointmentSelectionPhase"
        mode="tertiary"
        icon-pos="left"
        :icon="PrimeIcons.ARROW_LEFT"
        :label="t('common.backToParam', { param: t('common.search') })"
        :loading="scheduleResponse.loading"
        class="back-button align-self-start"
        @click="onBack"
      />

      <div class="flex flex-row flex-gap-1">
        <InputWrapper
          v-if="hasSubdomains"
          v-slot="{ ariaProps, inputClasses, inputId }"
          :input-id="`${uid}-domain-select`"
          :label="t('clients.clients')"
          class="w-4"
        >
          <RygenDropdown
            :id="inputId"
            v-model="criteria.ownerNaturalIds[0]"
            v-bind="ariaProps"
            :class="inputClasses"
            :disabled="isInAppointmentSelectionPhase"
            :options="subdomains"
            :placeholder="t('clients.clients')"
            option-label="name"
            option-value="naturalId"
            append-to="body"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId }"
          :input-id="`${uid}-carrier-select`"
          :label="t('carriers.carriers')"
          class="w-4"
        >
          <RygenDropdown
            :id="inputId"
            v-model="criteria.scac"
            v-bind="ariaProps"
            :class="inputClasses"
            :disabled="isInAppointmentSelectionPhase"
            :options="filteredCarriers"
            :placeholder="t('carriers.carriers')"
            option-label="label"
            option-value="value"
            append-to="body"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ inputId }"
          :input-id="`${uid}-pickup-location-select`"
          :label="t('parcelScheduling.pickupLocations')"
          class="w-4"
        >
          <ParcelPickupLocationQuickSearch
            :id="inputId"
            v-model="origin"
            append-to="body"
            :placeholder="t('parcelScheduling.pickupLocations')"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ inputId }"
          :input-id="`${uid}-date-select`"
          :label="t('common.dates')"
        >
          <RygenCalendar
            :id="inputId"
            :model-value="userSelectedDate"
            :time-zone="timeZone"
            :disabled="isInAppointmentSelectionPhase"
            :placeholder="t('common.dates')"
            @date-select="setPickupRange"
          />
        </InputWrapper>
      </div>

      <ParcelAppointmentSelectionPhase
        v-if="isInAppointmentSelectionPhase"
        v-model:columns="columns"
        v-model:pickup-date="pickupDate"
        v-model:pickup-time-earliest="pickupTimeEarliest"
        v-model:pickup-time-latest="pickupTimeLatest"
        v-model:selected-shipments="selectedShipments"
        :criteria="criteria"
        :pickup-date-options="pickupDateOptions"
        :pickup-time-earliest-options="pickupTimeEarliestOptions"
        :pickup-time-latest-options="pickupTimeLatestOptions"
        :results="selectedShipmentsAsSearchResultLoadable"
      />
      <ParcelShipmentSearch
        v-else-if="hasRequiredSearchValues"
        v-model:model-value="criteria"
        v-model:selected-shipments="selectedShipments"
        v-model:columns="columns"
        :fuzzy-results="fuzzyResults"
        :results="results"
        :user-selected-date="userSelectedDate"
        @page="onPage"
        @sort="onSort"
        @refresh-search="runSearchDebounced"
      />
    </template>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />
      <RygenButton
        v-if="!isInAppointmentSelectionPhase"
        mode="primary"
        :icon="PrimeIcons.ARROW_RIGHT"
        :label="
          t('common.selectParam', {
            param: t('shipments.shipments'),
          })
        "
        :disabled="selectedShipments.length < 1"
        :loading="pickupOptions.loading"
        @click="onSelectShipments"
      />
      <RygenButton
        v-else
        mode="primary"
        icon="pi pi-save"
        :label="t('parcelScheduling.schedulePickup')"
        :disabled="!hasRequiredPickupInfo"
        :loading="scheduleResponse.loading"
        @click="onSchedule"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import axios, { CancelTokenSource } from 'axios';
import debounce from 'lodash/debounce';
import { DateTime, Duration } from 'luxon';
import { computed, h, ref, Ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast, useUid } from '@rygen/rygen-ui/composables';
import { Environment } from '@rygen/rygen-ui/env/Environment';
import environmentPropertiesStore from '@rygen/rygen-ui/env/EnvironmentPropertiesStore';
import { renderDateTime } from '@rygen/rygen-ui/i18n';
import {
  ColumnSpecification,
  DomainReference,
  DomainTree,
  DomainType,
  DropdownOption,
  Loadable,
  Location,
  ZonedDateTimeRange,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  ApiParcelPickupAvailabilityDetails,
  CarrierPickupAvailabilityOptions,
  COST_ESTIMATE_COLUMN,
  MessageSpecification,
  ParcelSchedulingError,
  ParcelSchedulingErrorResponse,
  ParcelSchedulingResponse,
  REQUESTED_PICKUP_DATE_RANGE_COLUMN,
  SERVICE_COLUMN,
  Shipment,
  ShipmentSearchCriteria,
  ShipmentSearchData,
  ShipmentSearchResult,
  ShipmentStatus,
  WEIGHT_COLUMN,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';

import ParcelAppointmentSelectionPhase from '@/components/shipment/parcel/ParcelAppointmentSelectionPhase.vue';
import ParcelPickupLocationQuickSearch from '@/components/shipment/parcel/ParcelPickupLocationQuickSearch.vue';
import ParcelShipmentSearch from '@/components/shipment/parcel/ParcelShipmentSearchPhase.vue';
import MessageContainer from '@/components/ui/MessageContainer.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';

const DEFAULT_QUERY_OPTIONS = {
  queryParam: 'search',
  searchDelay: 500,
  defaultCriteria: {
    modes: ['PARCEL'],
    statuses: [ShipmentStatus.BOOKED],
  },
};

// Carriers that accept or reject a specific requested pickup time rather than
// returning availability windows. For these we synthesize selectable time
// options locally and rely on the schedule call's accept/reject response
// (see onSchedule) to keep the modal open and re-prompt on rejection.
const SCACS_WITHOUT_AVAILABILITY_WINDOWS = new Set([
  'UPSN', // UPS
  'DHLE', // DHL Express
  'LMEX', // Loomis Express
  'GLSU', // GLS,
  'TEST', // Testing SCAC, only in lower envs
]);

// Carriers gated behind the expanded-parcel-scheduling-feat-flag flag. UPS
// continues to be gated independently by the existing upsScheduling flag.
const EXPANDED_PARCEL_SCHEDULING_SCACS = new Set(['DHLE', 'LMEX', 'GLSU']);

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const emit = defineEmits<{
  (event: 'success', result: ParcelSchedulingResponse): void;
}>();

const columns: Ref<ColumnSpecification[]> = ref([
  REQUESTED_PICKUP_DATE_RANGE_COLUMN,
  SERVICE_COLUMN,
  WEIGHT_COLUMN,
  COST_ESTIMATE_COLUMN,
]);
const carriers: Ref<DropdownOption<string>[]> = ref([
  { label: 'FedEx Express', value: 'FDXE' },
  { label: 'FedEx Ground', value: 'FDXG' },
  { label: 'UPS', value: 'UPSN' },
  { label: 'USPS', value: 'USPS' },
  { label: 'DHL Express', value: 'DHLE' },
  { label: 'Loomis Express', value: 'LMEX' },
  { label: 'GLS', value: 'GLSU' },
  { label: 'TEST', value: 'TEST' },
]);
const pickupLocations: Ref<Loadable<Location[]>> = ref({
  loading: false,
  data: [],
});
const pickupOptions: Ref<Loadable<ApiParcelPickupAvailabilityDetails>> = ref({
  loading: false,
});
const scheduleResponse: Ref<Loadable<ParcelSchedulingResponse>> = ref({
  loading: false,
});
const selectedLocation: Ref<Location | undefined> = ref();
const userSelectedDate: Ref<DateTime | undefined> = ref();
const selectedShipments: Ref<ShipmentSearchData[]> = ref([]);
const pickupDate: Ref<DateTime | undefined> = ref();
const pickupTimeEarliest: Ref<DateTime | undefined> = ref();
const pickupTimeLatest: Ref<DateTime | undefined> = ref();

//Shipment search constants
const results: Ref<Loadable<ShipmentSearchResult>> = ref({
  loading: false,
  data: new ShipmentSearchResult(),
});
const fuzzyResults: Ref<Loadable<ShipmentSearchResult>> = ref({
  loading: false,
  data: new ShipmentSearchResult(),
});

const criteria: Ref<ShipmentSearchCriteria> = ref(
  new ShipmentSearchCriteria(DEFAULT_QUERY_OPTIONS.defaultCriteria),
);
const cancelRequest: Ref<CancelTokenSource | undefined> = ref();
const runSearchDebounced = debounce(
  () => runSearch(criteria.value, cancelRequest.value),
  DEFAULT_QUERY_OPTIONS.searchDelay,
);

const { t } = useI18n();
const toast = useToast();
const { uid } = useUid('parcel-scheduling-modal');
const { availableDomainMap, selectedDomain } = useCurrentUserStore();
const upsSchedulingEnabled = useFlag('upsScheduling');
const uspsSchedulingEnabled = useFlag('usps-scheduling-feat-flag');
const expandedParcelSchedulingEnabled = useFlag(
  'expanded-parcel-scheduling-feat-flag',
);

// Note: UPS (UPSN) shares the synthesized-options flow with the expanded
// carriers (see SCACS_WITHOUT_AVAILABILITY_WINDOWS) but is gated separately by
// its pre-existing upsScheduling flag — which is why it is NOT in
// EXPANDED_PARCEL_SCHEDULING_SCACS.
const filteredCarriers = computed(() => {
  const environment = environmentPropertiesStore.getValue('ENVIRONMENT_NAME');
  return carriers.value.filter((c) => {
    if (c.value === 'UPSN') {
      return upsSchedulingEnabled.value;
    }
    if (c.value === 'USPS') {
      return uspsSchedulingEnabled.value;
    }
    if (c.value === 'TEST') {
      return (
        Environment.LOCAL === environment ||
        Environment.DEV === environment ||
        Environment.QA === environment
      );
    }
    if (EXPANDED_PARCEL_SCHEDULING_SCACS.has(c.value)) {
      return expandedParcelSchedulingEnabled.value;
    }
    return true;
  });
});

const subdomains = computed(() =>
  getDomainAndDescendantsForDomain(selectedDomain.value).filter(
    (d) => d.domainType === DomainType.Client,
  ),
);

const hasSubdomains = computed(() =>
  selectedDomain.value?.domainType === DomainType.Client
    ? subdomains.value.length > 1
    : subdomains.value.length > 0,
);

const hasPickupLocation = computed(() => {
  return (
    !!criteria.value.originLocationId ||
    !!criteria.value.originLocationCode ||
    !!criteria.value.originLocationName
  );
});

const hasRequiredSearchValues = computed(() => {
  if (hasSubdomains.value && !criteria.value.ownerNaturalIds[0]) {
    return false;
  }
  return (
    !!criteria.value.requestedPickupDateRange &&
    !!criteria.value.scac &&
    hasPickupLocation.value
  );
});

const hasRequiredPickupInfo = computed(() => {
  return (
    !!pickupDate.value && !!pickupTimeEarliest.value && !!pickupTimeLatest.value
  );
});

const isInAppointmentSelectionPhase = computed(
  () => availabilityOptions.value.length > 0,
);

const timeZone = computed(() => selectedLocation.value?.timeZone ?? undefined);

const selectedShipmentsAsSearchResultLoadable = computed(() => {
  return {
    loading: false,
    data: new ShipmentSearchResult({ content: selectedShipments.value }),
  };
});

const availabilityOptions = computed(
  () => pickupOptions.value.data?.availabilityOptions ?? [],
);

const pickupDateOptions = computed(() => {
  const rawOptions = availabilityOptions.value.map(
    (o) => o.availablePickupDate,
  );

  return rawOptions?.map((o) => {
    return {
      label: renderDateTime(o, {
        timeFormat: 'NONE',
        timeZone: timeZone.value,
      }),
      value: o,
    };
  });
});

const selectedDateAvailabilityOption = computed(() =>
  availabilityOptions.value.find(
    (o) => pickupDate.value === o.availablePickupDate,
  ),
);

const pickupTimeEarliestOptions = computed(() => {
  const rawOptions =
    selectedDateAvailabilityOption.value?.availableEarliestPickupTimes;

  return rawOptions?.map((o) => {
    return {
      label: renderDateTime(o, {
        timeFormat: { hour: 'numeric', minute: 'numeric' },
        dateFormat: 'NONE',
        timeZone: timeZone.value,
      }),
      value: o,
    };
  });
});

const pickupTimeLatestOptions = computed(() => {
  const rawOptions =
    selectedDateAvailabilityOption.value?.availableLatestPickupTimes;

  return rawOptions?.map((o) => {
    return {
      label: renderDateTime(o, {
        timeFormat: { hour: 'numeric', minute: 'numeric' },
        dateFormat: 'NONE',
        timeZone: timeZone.value,
      }),
      value: o,
    };
  });
});

const origin = computed({
  get() {
    if (originLocationUndefined()) {
      return undefined;
    }
    return selectedLocation.value;
  },
  set(newVal: Location) {
    criteria.value.originLocationId = newVal.id;
    selectedLocation.value = newVal;
  },
});

const getDomainAndDescendantsForDomain = (
  currentDomain: DomainReference | undefined,
) => {
  const recursiveFlatMap = (domainTree: DomainTree): DomainReference[] => {
    return [
      domainTree.asDomainReference(),
      ...domainTree.children.flatMap(recursiveFlatMap),
    ];
  };

  if (!currentDomain?.id) {
    return [];
  }

  const currentDomainTree = availableDomainMap.value.get(currentDomain.id);

  if (currentDomainTree == null) {
    return [];
  }

  return recursiveFlatMap(currentDomainTree);
};

const runSearch = async (
  criteria: ShipmentSearchCriteria,
  cancelRequest?: CancelTokenSource,
) => {
  if (!criteria.requestedPickupDateRange) {
    return;
  }

  const fuzzyDateRange = createFuzzyPickupRange(
    criteria.requestedPickupDateRange,
  );
  const fuzzyCriteria = new ShipmentSearchCriteria({
    ...criteria,
    requestedPickupDateRange: fuzzyDateRange,
    requestedPickupDateRangeToExclude: criteria.requestedPickupDateRange,
  });

  try {
    results.value.loading = true;
    fuzzyResults.value.loading = true;
    results.value.data = await api.shipmentParcel.unscheduledParcelSearch(
      criteria,
      cancelRequest,
    );
    fuzzyResults.value.data = await api.shipmentParcel.unscheduledParcelSearch(
      fuzzyCriteria,
      cancelRequest,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    results.value.error = e;
    console.error('Error loading shipments', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('shipments.shipments', 2),
      }),
      error: e,
    });
  } finally {
    results.value.loading = false;
    fuzzyResults.value.loading = false;
  }
};

const originLocationUndefined = () => {
  return (
    criteria.value.originLocationId == null &&
    criteria.value.originLocationName == null &&
    criteria.value.originLocationCode == null &&
    criteria.value.originCity == null &&
    criteria.value.originState == null &&
    criteria.value.originPostalCode == null &&
    criteria.value.originCountry == null
  );
};

const extractProviderErrors = (error: any): ParcelSchedulingError[] => {
  if (error == null) {
    return [];
  }

  if (!axios.isAxiosError(error)) {
    return (error as Array<any>)?.map((e) => {
      return {
        message: e.message ?? 'Unknown Error',
        errorCode: e.errorCode ?? 'UNKNOWN',
      };
    });
  }

  const errorData = error.response?.data as any;
  const errorResponse = new ParcelSchedulingErrorResponse(
    errorData?.errorDetails,
  );
  return errorResponse.errors.filter((x) => x?.message);
};

const schedulingErrors = computed(() => {
  return [
    ...extractProviderErrors(pickupOptions.value.error),
    ...extractProviderErrors(scheduleResponse.value.error),
  ];
});

const schedulingErrorMessages = computed((): MessageSpecification[] => {
  return schedulingErrors.value.map((e) => {
    return {
      id: e.errorCode + ' ' + e.message,
      textSlot: () => {
        const message = h('span', { class: 'font-bold' }, e.message);
        const code = h('span', t('common.codes') + ': ' + e.errorCode);
        return h('div', { class: 'flex flex-column flex-gap-1' }, [
          message,
          code,
        ]);
      },
      severity: 'error',
    };
  });
});

const createFuzzyPickupRange = (original: ZonedDateTimeRange | undefined) => {
  if (!original) {
    return undefined;
  }

  const newRange = new ZonedDateTimeRange(original);
  newRange.start = original.start?.minus(Duration.fromObject({ days: 3 }));
  newRange.end = original.end?.plus(Duration.fromObject({ days: 3 }));
  return newRange;
};

const setPickupRange = (date: DateTime) => {
  userSelectedDate.value = date;

  criteria.value.requestedPickupDateRange = new ZonedDateTimeRange({
    start: DateTime.fromObject(
      {
        year: date.year,
        month: date.month,
        day: date.day,
        hour: 0,
        minute: 0,
        second: 0,
      },
      { zone: timeZone.value },
    ),
    end: DateTime.fromObject(
      {
        year: date.year,
        month: date.month,
        day: date.day,
        hour: 23,
        minute: 59,
        second: 59,
      },
      { zone: timeZone.value },
    ),
  });
};

const resetFields = () => {
  criteria.value = new ShipmentSearchCriteria({
    modes: ['PARCEL'],
    statuses: ['BOOKED'],
  });
  pickupLocations.value = { loading: false };
  selectedLocation.value = undefined;
  userSelectedDate.value = undefined;
  pickupOptions.value = { loading: false };
  scheduleResponse.value = { loading: false };
  selectedShipments.value = [];
  pickupDate.value = undefined;
  pickupTimeEarliest.value = undefined;
  pickupTimeLatest.value = undefined;
};

const onBack = () => {
  pickupOptions.value = { loading: false };
  scheduleResponse.value.error = [];
  pickupDate.value = undefined;
  pickupTimeEarliest.value = undefined;
  pickupTimeLatest.value = undefined;
};

const onSelectShipments = async () => {
  if (userSelectedDate.value == null || selectedShipments.value.length < 1) {
    return;
  }

  pickupOptions.value.error = [];

  if (
    criteria.value.scac != null &&
    SCACS_WITHOUT_AVAILABILITY_WINDOWS.has(criteria.value.scac)
  ) {
    pickupOptions.value.data = generateDateTimeOptions(userSelectedDate.value);
    return;
  }

  //The ids can never be null but Typescript complains if we don't filter them
  const shipmentIds =
    selectedShipments.value.map((s) => s.id).filter((id) => id != null) ?? [];
  try {
    pickupOptions.value.loading = true;
    pickupOptions.value.data =
      await api.shipmentParcel.getApiParcelPickupAvailabilityDetails(
        userSelectedDate.value,
        undefined,
        shipmentIds,
      );
    if (pickupOptions.value.data.errors.length > 0) {
      pickupOptions.value.error = pickupOptions.value.data.errors;
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('parcelScheduling.availabilityWindows', 2),
        }),
        error: undefined,
      });
    }
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching parcel availability windows', e);
    pickupOptions.value.error = e as Error;
    toast.addError({
      detail:
        t('errors.loadingParam', {
          param: t('parcelScheduling.availabilityWindows', 2),
        }) +
        '\n' +
        (e.response?.data?.exceptionMessage ?? ''),
      error: e,
    });
  } finally {
    pickupOptions.value.loading = false;
  }
};

const onSchedule = async () => {
  if (
    !pickupDate.value ||
    !pickupTimeEarliest.value ||
    !pickupTimeLatest.value
  ) {
    return;
  }
  //The ids can never be null but Typescript complains if we don't filter them
  const shipmentIds =
    selectedShipments.value.map((s) => s.id).filter((id) => id != null) ?? [];
  const pickupStartTime = getDateTimeCombo(
    pickupDate.value,
    pickupTimeEarliest.value,
  );
  const pickupEndTime = getDateTimeCombo(
    pickupDate.value,
    pickupTimeLatest.value,
  );

  try {
    scheduleResponse.value.loading = true;
    scheduleResponse.value.error = [];
    scheduleResponse.value.data = await api.shipmentParcel.scheduleParcelPickup(
      pickupStartTime,
      pickupEndTime,
      shipmentIds,
    );
    if (!scheduleResponse.value.data.success) {
      scheduleResponse.value.error = scheduleResponse.value.data.errors;
      toast.addError({
        detail: t('parcelScheduling.errors.schedulingPickup'),
        error: undefined,
      });
    } else {
      toast.addSuccess({ detail: t('parcelScheduling.successfullyScheduled') });
      scheduleResponse.value.data.shipmentIds = shipmentIds;
      emit('success', scheduleResponse.value.data);
      isVisible.value = false;
    }
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error scheduling parcel pickup', e);
    scheduleResponse.value.error = e as Error;
    toast.addError({
      detail:
        t('parcelScheduling.errors.schedulingPickup') +
        '\n' +
        (e.response?.data?.exceptionMessage ?? ''),
      error: e,
    });
  } finally {
    scheduleResponse.value.loading = false;
  }
};

const getDateTimeCombo = (date: DateTime, time: DateTime) => {
  return DateTime.fromObject(
    {
      year: date.year,
      month: date.month,
      day: date.day,
      hour: time.hour,
      minute: time.minute,
      second: time.second,
    },
    { zone: timeZone.value },
  );
};

const onPage = (event: DataTablePageEvent) => {
  criteria.value.pageSize = event.rows as number;
  criteria.value.page = event.page as number;
};

const onSort = (event: DataTableSortEvent) => {
  criteria.value.sortField = event.sortField as keyof Shipment | undefined;
  criteria.value.sortOrder = (event.sortOrder ?? 0) as -1 | 0 | 1;
};

// Builds selectable pickup time options locally for carriers that accept/reject
// a requested time instead of returning availability windows (see
// SCACS_WITHOUT_AVAILABILITY_WINDOWS).
const generateDateTimeOptions = (
  selectedDate: DateTime,
): ApiParcelPickupAvailabilityDetails => {
  const secondDate = selectedDate.plus({ days: 1 });
  const thirdDate = secondDate.plus({ days: 1 });
  const earliestTimeOptions: DateTime[] = [];
  const latestTimeOptions: DateTime[] = [];

  //Give users time options between 7am and 8pm for earliest, 8am and 9pm for latest
  for (let i = 7; i <= 20; i++) {
    earliestTimeOptions.push(
      selectedDate.set({ hour: i, minute: 0, second: 0 }),
    );
    latestTimeOptions.push(
      selectedDate.set({ hour: i + 1, minute: 0, second: 0 }),
    );
  }

  return new ApiParcelPickupAvailabilityDetails({
    errors: [],
    availabilityOptions: [
      new CarrierPickupAvailabilityOptions({
        availablePickupDate: selectedDate,
        availableEarliestPickupTimes: earliestTimeOptions,
        availableLatestPickupTimes: latestTimeOptions,
      }),
      new CarrierPickupAvailabilityOptions({
        availablePickupDate: secondDate,
        availableEarliestPickupTimes: earliestTimeOptions,
        availableLatestPickupTimes: latestTimeOptions,
      }),
      new CarrierPickupAvailabilityOptions({
        availablePickupDate: thirdDate,
        availableEarliestPickupTimes: earliestTimeOptions,
        availableLatestPickupTimes: latestTimeOptions,
      }),
    ],
  });
};

watch(pickupDate, () => {
  pickupTimeEarliest.value = undefined;
  pickupTimeLatest.value = undefined;
});

watch(isVisible, (newVal: boolean, oldVal: boolean) => {
  if (!newVal && oldVal) {
    resetFields();
  }
});

watch(hasRequiredSearchValues, (newVal: boolean) => {
  if (newVal) {
    runSearchDebounced();
  }
});

watch(
  criteria,
  () => {
    if (hasRequiredSearchValues.value) {
      selectedShipments.value = [];
      scheduleResponse.value.error = [];
      pickupOptions.value.error = [];
      runSearchDebounced();
    }
  },
  { deep: true },
);
</script>
